import { UploadFile } from "antd";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { MediaProject } from "../types";
import { Step, OBJECTIVES_STEP } from "./components/ProjectCreateWizard";

interface State {
  step: Step;
  file: (UploadFile & { abort?: () => void }) | null;
  uploadedFileNames: string[];
  uploadedFiles: UploadFile[];
  project: MediaProject | null;
  wizardStartTime: number | null;
  hasNoProjects: boolean;
  playbookBypass: { id: string; name: string } | null;
}

type Actions = {
  setStep: (step: Step) => void;
  setFile: (file: (UploadFile & { abort?: () => void }) | null) => void;
  setUploadedFileNames: (names: string[]) => void;
  setUploadedFiles: (files: UploadFile[]) => void;
  setProject: (project: MediaProject | null) => void;
  setWizardStartTime: (time: number | null) => void;
  setHasNoProjects: (value: boolean) => void;
  setPlaybookBypass: (value: { id: string; name: string } | null) => void;
  resetProjectCreateState: () => void;
};

const initialState = {
  step: OBJECTIVES_STEP,
  file: null,
  uploadedFileNames: [],
  uploadedFiles: [],
  project: null,
  wizardStartTime: null,
  hasNoProjects: false,
  playbookBypass: null,
} satisfies State;

export const useProjectWizardStore = create<State & Actions>()(
  devtools(
    persist(
      immer((set) => {
        return {
          ...initialState,
          resetProjectCreateState: () => set(initialState),
          setStep: (step) =>
            set((state) => {
              state.step = step;
            }),
          setFile: (file) =>
            set((state) => {
              state.file = file;
            }),
          setUploadedFileNames: (names) =>
            set((state) => {
              state.uploadedFileNames = names;
            }),
          setUploadedFiles: (files) =>
            set((state) => {
              state.uploadedFiles = files;
            }),
          setProject: (project) =>
            set((state) => {
              state.project = project;
            }),
          setWizardStartTime: (time) =>
            set((state) => {
              state.wizardStartTime = time;
            }),
          setHasNoProjects: (value) =>
            set((state) => {
              state.hasNoProjects = value;
            }),
          setPlaybookBypass: (value) =>
            set((state) => {
              state.playbookBypass = value;
            }),
        };
      }),
      {
        name: "project-wizard-store",
        storage: createJSONStorage(() => sessionStorage),
        partialize: ({ uploadedFiles, ...rest }) => rest,
      }
    )
  )
);
